/**
 * @module components/feedback/SectionErrorBoundary
 * @description Reusable React Error Boundary isolating page and widget-level render
 * failures so navigation remains functional and users can recover via retry.
 */
import * as React from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface SectionErrorBoundaryProps {
  children: React.ReactNode;
  /** Human-readable section title shown in the fallback alert when a render error occurs. */
  sectionTitle?: string;
  /** Changing resetKey automatically clears the error state (e.g. route pathname or clientId). */
  resetKey?: string | number;
}

interface SectionErrorBoundaryState {
  hasError: boolean;
}

/**
 * Isolates unexpected render exceptions inside a feature section or page without
 * crashing the surrounding application shell. Never renders raw stack traces to users.
 */
export class SectionErrorBoundary extends React.Component<
  SectionErrorBoundaryProps,
  SectionErrorBoundaryState
> {
  public constructor(props: SectionErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  public static getDerivedStateFromError(): SectionErrorBoundaryState {
    return { hasError: true };
  }

  public componentDidUpdate(prevProps: SectionErrorBoundaryProps): void {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  public componentDidCatch(): void {
    // Intentionally omit raw error object logging to console in production UI
    // to prevent accidental leakage of sensitive form or network state (OWASP).
  }

  private handleReset = (): void => {
    this.setState({ hasError: false });
  };

  public render(): React.ReactNode {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          className="rounded-2xl border border-danger/20 bg-danger-soft p-6 sm:p-8 text-center text-danger shadow-card"
        >
          <AlertCircle className="mx-auto h-8 w-8 text-danger" aria-hidden="true" />
          <h2 className="mt-3 text-base font-medium text-foreground">
            {this.props.sectionTitle ?? "Unable to display this section"}
          </h2>
          <p className="mt-1 text-xs text-muted-foreground max-w-md mx-auto">
            Something unexpected interrupted rendering. Your session and data remain safe.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={this.handleReset}
            className="mt-4 bg-card text-foreground text-xs gap-1.5"
          >
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            <span>Try Again</span>
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

